import { z } from 'zod';
import { zErrorCode } from './errors.js';
import { zMwStage, zSeatId, zTeamId } from './enums.js';
import { zGmMessagePayload, zPhaseState } from './game.js';
import { zCellOrdersState, zOrdersDraftPayload, zOrdersSubmitPayload, zPublishContactPayload, zTeamOrdersState, } from './orders.js';
import { zSeatIdentity } from './session.js';
import { zCellView, zGmView, zIntelReport, zMwObservation } from './view.js';
/**
 * WebSocket protocol (§8.4).
 *
 * Every frame is `{ v, seq, type, payload }`, Zod-validated on BOTH ends. `seq` is
 * a per-connection monotonic server sequence number; the client sends it back in
 * `resume` after a drop so the server can replay the gap instead of re-sending a
 * whole snapshot (§8.5).
 *
 * M0 implemented the transport-level subset: hello, clock sync, and errors. M2
 * filled in everything except the Missile Warning trio; M4 added those, and
 * `view.patch` is the only reserved type left.
 *
 * Two M2 decisions live here:
 *
 *  - **`seq` is per seat, not per connection** (decision M2-D7). A counter that
 *    restarts at zero on reconnect makes `resume` meaningless: the server cannot
 *    tell "I missed frames 40–47" from "this is a fresh socket". A seat's sequence
 *    is monotonic for the seat's whole life, across any number of sockets.
 *  - **There is no `view.patch`** (decision M2-D6). Every change sends a full
 *    `view.snapshot`. A patch applied to a baseline the client does not actually
 *    hold produces a view that is silently wrong, and a wrong fog-of-war view is
 *    worse than a slow one. The type stays reserved.
 *
 * **v2 (M5): the cyber seat is `CW`, not `CYBER`.** `SeatId` is on the wire —
 * in `hello.seat`, in the claim path, in every `visibleTo` tag — so a client built
 * before that rename and a server built after it disagree about a seat's name.
 * Nothing about the frame envelope moved; the bump exists so `/api/version` can
 * say so and `ProtocolMismatchScreen` can stop a stale bundle instead of letting
 * it fail at claim time. RUNBOOK §2.0's order (server, then Pages) still applies.
 */
export const WS_PROTOCOL_VERSION = 2;
/**
 * Hard cap on a single inbound frame. §16.5: a malicious `orders.draft` flood is
 * the only realistic DoS from an authenticated seat. A full order set for one cell
 * is a few KB; 64 KiB is generous and still bounds the damage.
 */
export const WS_MAX_MESSAGE_BYTES = 64 * 1024;
/** §8.5 heartbeat: 20 s ping, 45 s dead-connection timeout, seat held 120 s after. */
export const WS_HEARTBEAT_INTERVAL_MS = 20_000;
export const WS_DEAD_CONNECTION_MS = 45_000;
export const SEAT_GRACE_MS = 120_000;
/** Clients re-sync their clock offset on connect and every 30 s (§8.2). */
export const WS_CLOCK_SYNC_INTERVAL_MS = 30_000;
// ---------------------------------------------------------------------------
// Envelope
// ---------------------------------------------------------------------------
function envelope(type, payload) {
    // `.strict()` is load-bearing, not tidiness (decision M2-D5): the server parses
    // every outbound frame through this union before writing it, so a stray
    // top-level field becomes a 500 rather than shipping.
    return z
        .object({
        v: z.literal(WS_PROTOCOL_VERSION),
        seq: z.number().int().nonnegative(),
        type: z.literal(type),
        payload,
    })
        .strict();
}
/**
 * Cheap pre-parse used to route a frame and to produce a useful error for an
 * unknown `type` without failing the whole discriminated union opaquely.
 */
export const zWsEnvelopeHeader = z.object({
    v: z.number().int(),
    seq: z.number().int().nonnegative(),
    type: z.string().min(1).max(64),
    payload: z.unknown(),
});
// ---------------------------------------------------------------------------
// Server → client
// ---------------------------------------------------------------------------
/**
 * Sent once, immediately on connect. Carries the server clock so the client's
 * first countdown render is already corrected rather than being a second wrong
 * (§8.2).
 */
export const zSessionHelloPayload = z
    .object({
    connectionId: z.string(),
    serverTimeMs: z.number().int(),
    protocolVersion: z.literal(WS_PROTOCOL_VERSION),
    contentVersion: z.string(),
    contentHash: z.string(),
    heartbeatIntervalMs: z.number().int().positive(),
    /** Null on an unauthenticated socket — the M0 connectivity console still uses one. */
    seat: zSeatIdentity.nullable(),
    /** Null until the socket is attached to a game. */
    game: zPhaseState.nullable(),
    /**
     * The seat's next outbound sequence number. A reconnecting client compares it
     * with its own `lastSeq` to decide whether to ask for a replay (decision M2-D7).
     */
    nextSeq: z.number().int().nonnegative(),
})
    .strict();
/**
 * Clock-sync reply. Echoes the client's send time so the client can compute
 * round-trip time and offset without keeping its own outstanding-ping table.
 */
export const zPongPayload = z
    .object({
    clientSentAtMs: z.number().int(),
    serverTimeMs: z.number().int(),
})
    .strict();
export const zWsErrorPayload = z
    .object({
    code: zErrorCode,
    message: z.string(),
    correlationId: z.string().optional(),
})
    .strict();
/**
 * A seat's whole picture. Sent on connect, on resume when the gap is too large to
 * replay, after every resolution, and after any intra-turn change (M2-D6).
 *
 * `view` is a `CellView` and nothing else. There is no branch of this schema that
 * accepts a `GmView` or a `WorldState`, which is what makes "GM data reached a
 * player socket" a compile error at the fan-out call site rather than a test
 * somebody has to have remembered to write (§6.1, decision M2-D4).
 */
export const zViewSnapshotPayload = z
    .object({
    view: zCellView,
    phase: zPhaseState,
    orders: zCellOrdersState.nullable(),
    team: zTeamOrdersState.nullable(),
    intel: zIntelReport.nullable(),
})
    .strict();
/**
 * §8.5: the GM sees connection state for all sixteen seats. Non-negotiable — the
 * umpire must know immediately that the REDFOR cyber player has dropped.
 *
 * Spelled out rather than composed as `zSeatIdentity.and(…)`: an intersection of
 * two `.readonly()` objects does not merge into a single strict object, so the
 * result rejects every value at runtime while still typechecking. Explicit is
 * duller and works.
 */
export const zGmSeatView = z
    .object({
    cell: zSeatId,
    team: z.union([zTeamId, z.literal('GM')]),
    isGm: z.boolean(),
    epoch: z.number().int(),
    displayName: z.string().nullable(),
    connected: z.boolean(),
})
    .strict()
    .readonly();
/** The umpire's snapshot. A separate type, carried by a separate message. */
export const zGmSnapshotPayload = z
    .object({
    view: zGmView,
    phase: zPhaseState,
    seats: z.array(zGmSeatView).readonly(),
})
    .strict();
export const zPhaseChangedPayload = zPhaseState;
export const zServerHello = envelope('session.hello', zSessionHelloPayload);
export const zServerPong = envelope('pong', zPongPayload);
export const zServerError = envelope('error', zWsErrorPayload);
export const zServerViewSnapshot = envelope('view.snapshot', zViewSnapshotPayload);
export const zServerGmSnapshot = envelope('gm.snapshot', zGmSnapshotPayload);
export const zServerPhaseChanged = envelope('phase.changed', zPhaseChangedPayload);
export const zServerIntelReport = envelope('intel.report', zIntelReport);
export const zServerOrdersState = envelope('orders.state', z.object({ own: zCellOrdersState.nullable(), team: zTeamOrdersState }).strict());
export const zServerGmMessage = envelope('gm.message', zGmMessagePayload);
export const zServerResumed = envelope('resume.result', z
    .object({
    replayedFrom: z.number().int().nonnegative(),
    replayedCount: z.number().int().nonnegative(),
    /** True when the gap was too large and a fresh snapshot follows (§8.5). */
    resnapshot: z.boolean(),
})
    .strict());
// ---------------------------------------------------------------------------
// Missile Warning (RB §5.3) — M4
// ---------------------------------------------------------------------------
/**
 * A stage firing (RB §5.3.4). Pushed to the warned team's MW seat and the GM, and
 * to nobody else.
 *
 * The payload carries only *this* stage's observations. The seat's full board
 * arrives in the `view.snapshot` that follows, which is the only thing that ever
 * carries a whole `CellView` (decision M2-D6) — this message exists so the seat can
 * animate the arrival rather than have a row appear silently.
 */
export const zMwStagePayload = z
    .object({
    eventId: z.string(),
    stage: zMwStage,
    observations: z.array(zMwObservation).readonly(),
})
    .strict();
/**
 * A release reaching FIRES and OW (RB §2.4, §5.3.6).
 *
 * This is the message that unlocks the interception path mid-window. It carries
 * `stageModifier` and never the total, for the reason `MwReleaseView` gives at
 * length (decision M4-D7).
 */
export const zMwReleasedPayload = z
    .object({
    eventId: z.string(),
    releasedAtStage: zMwStage,
    assignedIdentity: z.string(),
    stageModifier: z.number().int(),
    interceptable: z.boolean(),
})
    .strict();
export const zServerMwStage = envelope('mw.stage', zMwStagePayload);
export const zServerMwReleased = envelope('mw.released', zMwReleasedPayload);
/**
 * IDENTIFY / RELEASE / DISMISS (RB §5.3.4) — decision M4-D8.
 *
 * A **command, not an order**: it goes through the game actor, mutates the live
 * world and is visible to another seat immediately. A draft could do none of those,
 * and the release *stage* is a function of the server's receipt time, which a draft
 * has no notion of.
 *
 * The client sends no stage and no timestamp. Both are the server's, because a
 * client that could name its own release stage could claim `[+4]` at any moment
 * (§8.3).
 */
export const zMwActionPayload = z
    .object({
    eventId: z.string(),
    action: z.enum(['IDENTIFY', 'RELEASE', 'DISMISS']),
    /** A catalogue model id. Required for IDENTIFY and RELEASE, ignored by DISMISS. */
    identity: z.string().min(1).max(64).optional(),
})
    .strict();
export const zClientMwAction = envelope('mw.action', zMwActionPayload);
export const zServerMessage = z.discriminatedUnion('type', [
    zServerHello,
    zServerPong,
    zServerError,
    zServerViewSnapshot,
    zServerGmSnapshot,
    zServerPhaseChanged,
    zServerIntelReport,
    zServerOrdersState,
    zServerGmMessage,
    zServerResumed,
    zServerMwStage,
    zServerMwReleased,
]);
// ---------------------------------------------------------------------------
// Client → server
// ---------------------------------------------------------------------------
export const zPingPayload = z
    .object({
    clientSentAtMs: z.number().int(),
})
    .strict();
export const zClientPing = envelope('ping', zPingPayload);
/**
 * Reconnect. `lastSeq` is the highest **seat** sequence number the client saw
 * (decision M2-D7). The server replays the gap from its in-memory ring buffer, or
 * re-snapshots if the gap is larger than the buffer.
 */
export const zResumePayload = z.object({ lastSeq: z.number().int().nonnegative() }).strict();
export const zClientResume = envelope('resume', zResumePayload);
export const zClientOrdersDraft = envelope('orders.draft', zOrdersDraftPayload);
export const zClientOrdersSubmit = envelope('orders.submit', zOrdersSubmitPayload);
export const zClientPublishContact = envelope('publish.contact', zPublishContactPayload);
export const zClientMessage = z.discriminatedUnion('type', [
    zClientPing,
    zClientResume,
    zClientOrdersDraft,
    zClientOrdersSubmit,
    zClientPublishContact,
    zClientMwAction,
]);
// ---------------------------------------------------------------------------
// Reserved message types (§8.4) — still unimplemented.
// Declared so both ends agree on the spelling before either implements it.
// ---------------------------------------------------------------------------
/**
 * `view.patch` is deliberately unbuilt (decision M2-D6) and stays reserved. The
 * Missile Warning trio came off this list in M4 and is defined above.
 */
export const RESERVED_SERVER_MESSAGE_TYPES = ['view.patch'];
export const RESERVED_CLIENT_MESSAGE_TYPES = [];
// ---------------------------------------------------------------------------
// Helpers shared by both ends
// ---------------------------------------------------------------------------
/**
 * Estimate the offset between the client clock and the authoritative server clock
 * from one ping/pong round trip (§8.2).
 *
 * Assumes a symmetric path, which is wrong in detail and close enough at these
 * timescales: an error of half the RTT asymmetry on a countdown measured in
 * minutes. Add `offsetMs` to `Date.now()` to get server time.
 */
export function estimateClockOffsetMs(args) {
    const roundTripMs = args.clientReceivedAtMs - args.clientSentAtMs;
    const offsetMs = args.serverTimeMs + roundTripMs / 2 - args.clientReceivedAtMs;
    return { offsetMs: Math.round(offsetMs), roundTripMs };
}
//# sourceMappingURL=ws.js.map